import React, { useMemo, useRef, useState } from 'react'
import useResizeObserver from '@react-hook/resize-observer'

import { Box } from '../Box/Box'

import { HorizontalAlignment, Placement } from './types'

type OffsetContainerProps = {
    placement: Placement
    render: React.JSX.Element
    triggerRect: DOMRect
    hitPosition: [number, number] | undefined
    animatePresence?: boolean
    containerRef: React.MutableRefObject<HTMLDivElement | null>
    setIsAbove?: (isAbove: boolean) => void
    horizontalAlignment?: HorizontalAlignment
}

type Position = 'top' | 'bottom' | 'left' | 'right'
const positions: Position[] = ['top', 'bottom', 'left', 'right']

const DEBUG = false

const margin = 4

export const OverlayContainer = (props: OffsetContainerProps) => {
    const { triggerRect, hitPosition, render, placement, setIsAbove, horizontalAlignment } = props
    const ref = useRef<HTMLDivElement>(null)
    const wouldOverflowBottom = useRef<boolean | undefined>(undefined)
    props.containerRef.current = ref.current

    const contentSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })
    const anchorRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)

    const anchorStyle: { [key: string]: number | string | undefined } = useMemo(
        () => ({
            position: 'absolute',
            border: DEBUG ? `1px solid green` : undefined,
        }),
        [],
    )

    const safeArea = useMemo(
        () => ({
            left: 0,
            top: 0,
            right: window.innerWidth,
            bottom: window.innerHeight,
        }),
        [],
    )

    const containerStyle: { [key: string]: number | string | undefined } = useMemo(
        () => ({
            position: 'absolute',
            border: DEBUG ? `1px solid pink` : undefined,
            maxWidth: safeArea.right - safeArea.left - margin * 2,
            minWidth:
                placement === 'dropdown'
                    ? `${Math.min(
                          safeArea.right - safeArea.left - margin * 2,
                          triggerRect.width,
                      )}px`
                    : undefined,
        }),
        [placement, safeArea, triggerRect.width],
    )

    const computeStyles = () => {
        const size = contentSizeRef.current

        const anchorStyle: Record<Position, number | undefined> = {
            left: undefined,
            right: undefined,
            top: undefined,
            bottom: undefined,
        }

        const containerStyle: Record<Position, number | undefined> = {
            left: undefined,
            right: undefined,
            top: undefined,
            bottom: undefined,
        }

        if (!triggerRect || (size.width === 0 && size.height === 0)) {
            return {
                anchorStyle,
                containerStyle,
            }
        }

        if (placement === 'dropdown') {
            // Calculate available space above and below the trigger
            const spaceBelow = safeArea.bottom - triggerRect.bottom - margin
            const spaceAbove = triggerRect.top - safeArea.top - margin

            const fitsBelow = size.height <= spaceBelow
            const fitsAbove = size.height <= spaceAbove

            // Determine vertical position: prefer below, then above if it fits,
            // otherwise anchor to bottom of viewport so it's never cropped at the top
            if (fitsBelow) {
                // Fits below - place below trigger
                anchorStyle.top = triggerRect.bottom
                wouldOverflowBottom.current = false
            } else if (fitsAbove) {
                // Fits above - place above trigger
                anchorStyle.top = triggerRect.top - size.height
                wouldOverflowBottom.current = true
            } else {
                // Neither fits - anchor to bottom of viewport (content extends up, never cropped at top)
                anchorStyle.top = Math.max(
                    safeArea.top + margin,
                    safeArea.bottom - size.height - margin,
                )
                wouldOverflowBottom.current = false
            }

            // setIsAbove a single time to prevent re-renders resulting in postion flip flops, or if the height of the card changes based on user actions
            if (wouldOverflowBottom.current === undefined) {
                setIsAbove?.(anchorStyle.top < triggerRect.top)
            }

            const anchorRight = triggerRect.right
            const fitsRight = anchorRight - size.width > safeArea.left

            const anchorLeft = triggerRect.left
            const fitsLeft = anchorLeft + size.width < safeArea.right

            if (horizontalAlignment === 'center') {
                anchorStyle.left = triggerRect.left + triggerRect.width / 2 - size.width / 2
            }
            // ideally align horizontally on the right
            else if (fitsRight || !fitsLeft) {
                anchorStyle.left = anchorRight
                containerStyle.right = 0
            } else {
                anchorStyle.left = anchorLeft
                containerStyle.left = 0
            }

            // Clamp horizontal position to keep dropdown within safe area
            // (handles cases where trigger is partially/fully off-screen)
            if (containerStyle.right === 0) {
                // Content extends left from anchor - ensure it doesn't go past left edge
                const contentLeft = (anchorStyle.left ?? 0) - size.width
                if (contentLeft < safeArea.left + margin) {
                    anchorStyle.left = safeArea.left + margin + size.width
                }
                // Also ensure anchor doesn't place content past right edge
                if ((anchorStyle.left ?? 0) > safeArea.right - margin) {
                    anchorStyle.left = safeArea.right - margin
                }
            } else {
                // Content extends right from anchor - ensure it doesn't go past right edge
                const contentRight = (anchorStyle.left ?? 0) + size.width
                if (contentRight > safeArea.right - margin) {
                    anchorStyle.left = safeArea.right - margin - size.width
                }
                // Also ensure anchor doesn't place content past left edge
                if ((anchorStyle.left ?? 0) < safeArea.left + margin) {
                    anchorStyle.left = safeArea.left + margin
                }
            }
        }

        if (placement === 'vertical') {
            anchorStyle.top = triggerRect.top
            const topDiff = Math.max(0, (size.height ?? 0) - anchorStyle.top)
            containerStyle.bottom = -topDiff

            anchorStyle.left = triggerRect.right
            const leftDiff = Math.max(0, (size.width ?? 0) - anchorStyle.left)
            containerStyle.right = leftDiff
        }

        if (placement === 'horizontal') {
            anchorStyle.top = triggerRect.top - margin

            const diff = Math.max(0, anchorStyle.top + size.height - safeArea.bottom + margin)

            // readjust vertically so the bottom doesn't go outside the screen
            containerStyle.top = 0 - diff

            const anchorRight = triggerRect.right + margin
            const fitsRight = anchorRight + size.width < safeArea.right

            const anchorLeft = triggerRect.left - margin
            const fitsLeft = anchorLeft - size.width > safeArea.left

            // ideally align horizontally on the right
            if (fitsRight || !fitsLeft) {
                anchorStyle.left = anchorRight
                containerStyle.left = 0
            } else {
                anchorStyle.left = anchorLeft
                containerStyle.right = 0
            }
        }

        if (placement === 'pointer' && hitPosition) {
            anchorStyle.left = hitPosition[0]
            anchorStyle.top = hitPosition[1]
        }

        return {
            anchorStyle,
            containerStyle,
        }
    }

    const updateStyles = () => {
        const styles = computeStyles()
        Object.values([
            [anchorRef.current, styles.anchorStyle] as const,
            [containerRef.current, styles.containerStyle] as const,
        ]).forEach(([element, style]) => {
            if (element) {
                Object.values(positions).forEach((position) => {
                    element.style[position] =
                        typeof style[position] === 'number' ? `${style[position]}px` : ''
                })
            }
        })
    }

    const [isSizeInitialized, setIsSizeInitialized] = useState(false)

    useResizeObserver(ref as React.RefObject<HTMLElement>, (targetSize) => {
        const bounds = ref.current?.getBoundingClientRect()
        if (bounds) {
            contentSizeRef.current.width = bounds.width
            contentSizeRef.current.height = bounds.height

            updateStyles()
            setIsSizeInitialized(true)
        }
    })

    const initializedAnchorStyle = useMemo(
        () => ({ ...anchorStyle, opacity: isSizeInitialized ? 1 : 0 }),
        [anchorStyle, isSizeInitialized],
    )

    return (
        <Box absoluteFill pointerEvents="auto" zIndex="tooltips">
            {render && (
                <div ref={anchorRef} style={initializedAnchorStyle}>
                    <div ref={containerRef} style={containerStyle}>
                        <Box ref={ref} position="relative">
                            {render}
                        </Box>
                    </div>
                </div>
            )}
        </Box>
    )
}
